import { useCurationExperienceStore } from "@/features/experience-curation/stores/useCurationExperienceStore";
import { useCurrentExperienceHighlightsQuery } from "@/features/experience-curation/queries/useExperienceHighlightsQuery";
import { useCurrentExperienceRawFunctionalsQuery } from "@/features/experience-curation/queries/useExperienceRawFunctionalsQuery";
import { useHasCustomCommercialFieldChange } from "@/features/experience-curation/queries/useHasRawFieldChange";
import { useMasterData } from "@/stores/master-data";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";
import {
  getAddedItems,
  getRemovedItems,
  getDiffItems,
  mergeUnique,
  type ModifiedItem,
} from "@/features/experience-highlights/utils/highlights-diff-utils";
import {
  fromManageable,
  toManageable,
  normalizeMixedHighlight,
} from "@/features/experience-shared/highlights-v2/utils/highlight-adapter";
import type { HighlightItem, HighlightItemDiff } from "@/features/experience-shared/highlights-v2/types/highlight-item";
import type { ExperiencePremadeItem } from "@/composables/useExperienceMasterDataApi";
import type { GenericHighlight, HighlightsKey, MixedHighlightValue } from "@/types/Highlights";

const customFieldKeyMap: Record<
  HighlightsKey,
  "custom_highlights" | "custom_included" | "custom_non_included" | "custom_important_information"
> = {
  highlights: "custom_highlights",
  included: "custom_included",
  non_included: "custom_non_included",
  important_information: "custom_important_information",
};

type HighlightsFieldConfig = {
  id: string;
  fieldKey: HighlightsKey;
  masterDataStoreKey: string;
  searchPlaceholderKey: string;
};

/**
 * Converts V2 editor updates back to the action-based model consumed by curation persistence.
 * The shared adapter stays action-free for RAW; curation reconciles against its current model.
 */
function toCurationManageable(items: HighlightItem[], currentValue: MixedHighlightValue): MixedHighlightValue {
  const nextValue = toManageable(items);
  const currentCustomById = new Map(currentValue.custom.map((item) => [item.id, item]));
  const currentOrderById = new Map(
    currentValue.custom
      .filter((item) => item.action !== "DELETE" && item.action !== "REMOVE")
      .sort((first, second) => first.visualization_order - second.visualization_order)
      .map((item, index) => [item.id, index + 1])
  );
  const nextCustomIds = new Set(nextValue.custom.map((item) => item.id));

  const visibleCustom = nextValue.custom.map((item): GenericHighlight => {
    const currentItem = currentCustomById.get(item.id);
    if (currentItem == null) {
      return { ...item, action: "CREATE", language_code: "en" };
    }

    const isModified = currentItem.name !== item.name || currentOrderById.get(item.id) !== item.visualization_order;

    return isModified
      ? { ...currentItem, ...item, action: currentItem.action === "CREATE" ? "CREATE" : "EDIT" }
      : { ...currentItem, ...item };
  });

  const removedCustom = currentValue.custom.flatMap((item): GenericHighlight[] => {
    if (nextCustomIds.has(item.id) || item.action === "CREATE") {
      return [];
    }
    if (item.action === "DELETE" || item.action === "REMOVE") {
      return [item];
    }
    return [{ ...item, action: item.id ? "DELETE" : "REMOVE" }];
  });

  return { premade: nextValue.premade, custom: [...visibleCustom, ...removedCustom] };
}

export function useHighlightsField(config: HighlightsFieldConfig) {
  const curationExperienceStore = useCurationExperienceStore();
  const masterData = useMasterData();

  const highlightsQuery = useCurrentExperienceHighlightsQuery(config.fieldKey);
  const rawFunctionalsQuery = useCurrentExperienceRawFunctionalsQuery();

  const initialValue = computed(() => highlightsQuery.data.value?.value ?? { custom: [], premade: [] });
  const { model, hasChanges, isFilled, reset } = useComplexModelModelWithChanges(initialValue, {
    normalizer: normalizeMixedHighlight,
  });

  const highlightItems = computed<HighlightItem[]>({
    get: () => fromManageable(model.value),
    set: (items) => {
      model.value = toCurationManageable(items, model.value);
    },
  });

  const isRequired = computed(() => config.fieldKey === "highlights" || config.fieldKey === "included");
  const isReadonly = computed(() => curationExperienceStore.isReadonly);
  const showRawFields = computed(() => curationExperienceStore.showRawFields);

  const rawFunctionals = computed(
    () => rawFunctionalsQuery.data.value?.current[config.fieldKey] ?? { premade: [], custom: [] }
  );
  const publishedFunctionals = computed(
    () => rawFunctionalsQuery.data.value?.published[config.fieldKey] ?? { premade: [], custom: [] }
  );
  const hasRawUnpublishedChanges = computed(() => rawFunctionalsQuery.data.value?.hasUnpublishedChanges ?? false);

  // Pre-compute the merged display model for diff mode.
  // This includes both current and removed items so the diff view can show them.
  const rawFunctionalsForDisplay = computed<MixedHighlightValue>(() => {
    if (!hasRawUnpublishedChanges.value) return rawFunctionals.value;

    const premade = mergeUnique(publishedFunctionals.value.premade, rawFunctionals.value.premade, "code");
    const customDiff = getDiffItems(publishedFunctionals.value.custom, rawFunctionals.value.custom, "code");

    return { premade, custom: customDiff.merged };
  });

  // Pre-compute diff arrays for highlighting added/removed items
  const addedItemsPremade = computed<GenericHighlight[]>(() =>
    hasRawUnpublishedChanges.value
      ? getAddedItems(publishedFunctionals.value.premade, rawFunctionals.value.premade, "code")
      : []
  );

  const removedItemsPremade = computed<GenericHighlight[]>(() =>
    hasRawUnpublishedChanges.value
      ? getRemovedItems(publishedFunctionals.value.premade, rawFunctionals.value.premade, "code")
      : []
  );

  const customDiffResult = computed(() =>
    hasRawUnpublishedChanges.value
      ? getDiffItems(publishedFunctionals.value.custom, rawFunctionals.value.custom, "code")
      : {
          added: [] as GenericHighlight[],
          removed: [] as GenericHighlight[],
          modified: [] as ModifiedItem[],
          merged: [] as GenericHighlight[],
        }
  );

  const customFieldKey = customFieldKeyMap[config.fieldKey];
  const hasUnpublishedChange = useHasCustomCommercialFieldChange(customFieldKey);

  const hierarchicalGroups = computed(() => masterData.hierarchicalGroups);
  const masterdataOptions = computed(() => {
    const key = config.masterDataStoreKey as "highlights" | "included" | "importantInformation";
    return masterData[key];
  });
  const premadeMasterdataOptions = computed(() => masterdataOptions.value as ExperiencePremadeItem[]);

  // V2 read-only projection of the raw functionals shown in the left "Raw" column.
  // Diff metadata is presentation-only and is discarded by `toManageable` at the persistence boundary.
  const rawHighlightItems = computed<HighlightItem[]>(() =>
    fromManageable(rawFunctionalsForDisplay.value).map((item) => {
      const addedItems = item.kind === "premade" ? addedItemsPremade.value : customDiffResult.value.added;
      const removedItems = item.kind === "premade" ? removedItemsPremade.value : customDiffResult.value.removed;
      const modifiedItem =
        item.kind === "custom"
          ? customDiffResult.value.modified.find((modified) => modified.id === item.id || modified.id === item.code)
          : undefined;

      const hasSameIdentity = (candidate: GenericHighlight) =>
        candidate.code != null && item.code != null ? candidate.code === item.code : candidate.id === item.id;
      const isAdded = addedItems.some(hasSameIdentity);
      const isRemoved = removedItems.some(hasSameIdentity);
      const diff: HighlightItemDiff | undefined = modifiedItem
        ? { oldValue: modifiedItem.oldValue, newValue: modifiedItem.newValue }
        : isRemoved
        ? { oldValue: item.name, newValue: "" }
        : isAdded
        ? { oldValue: "", newValue: item.name }
        : undefined;

      return diff == null ? item : { ...item, diff };
    })
  );

  return {
    id: config.id,
    fieldKey: config.fieldKey,
    searchPlaceholderKey: config.searchPlaceholderKey,
    model,
    highlightItems,
    rawHighlightItems,
    isRequired,
    isReadonly,
    showRawFields,
    hasChanges,
    hasUnpublishedChange,
    isFilled,
    rawFunctionals,
    rawFunctionalsForDisplay,
    publishedFunctionals,
    hasRawUnpublishedChanges,
    addedItemsPremade,
    removedItemsPremade,
    customDiffResult,
    hierarchicalGroups,
    masterdataOptions,
    premadeMasterdataOptions,
    reset,
  };
}
